import { useState, useEffect } from 'react'
import { makeGetRequest } from '../../_state/helpers/api'
import { configWithAuth } from '../../_state/helpers'

function configWithAuthBlob() {
    const config = configWithAuth()
    config.responseType = 'blob'
    return config
}

// Module-level blob cache – survives component unmounts, cleared on page reload.
// Since blob URLs are tied to the page lifetime, this is safe and avoids
// re-fetching the same image when a card unmounts and remounts.
const blobCache: Record<string, string> = {}
const inflightRequests: Record<string, Promise<string>> = {}

/**
 * Hook that fetches a student-endpoint image (or any blob asset) and returns
 * a blob URL that can be used directly as an <img> src.
 *
 * Uses a module-level cache so the same URL is never fetched twice per session.
 *
 * @param assetUrl – relative API path such as "image/file/{fileReferenceId}"
 * @returns blob URL string, or null while loading / on error
 */
export function useStudentImage(assetUrl: string | null | undefined): string | null {
    const [blobUrl, setBlobUrl] = useState<string | null>(
        assetUrl ? blobCache[assetUrl] ?? null : null
    )

    useEffect(() => {
        if (!assetUrl) {
            setBlobUrl(null)
            return
        }

        // Already cached – use immediately
        if (blobCache[assetUrl]) {
            setBlobUrl(blobCache[assetUrl])
            return
        }

        let cancelled = false

        // De-duplicate: if the same URL is already being fetched by another
        // component instance, piggy-back on the same promise.
        if (!inflightRequests[assetUrl]) {
            inflightRequests[assetUrl] = makeGetRequest(assetUrl, configWithAuthBlob())
                .then((response: any) => {
                    const url = URL.createObjectURL(response.data)
                    blobCache[assetUrl] = url
                    return url
                })
                .catch((err: any) => {
                    console.warn('[useStudentImage] Failed to fetch:', assetUrl, err)
                    return ''
                })
                .finally(() => {
                    delete inflightRequests[assetUrl!]
                })
        }

        inflightRequests[assetUrl].then((url: string) => {
            if (!cancelled && url) {
                setBlobUrl(url)
            }
        })

        return () => {
            cancelled = true
        }
    }, [assetUrl])

    return blobUrl
}

export default useStudentImage
